import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { userIdentityShape } from '@bibliocommons/bc-prop-types';

import { selectAppConfig } from 'app/selectors/AppSelector';
import { createFollow, createIgnore, removeFollow, removeIgnore } from 'app/actions/RelationshipsActions';
import {
  selectSyncingFollowsByTargetUserId,
  selectSyncingIgnoresByTargetUserId,
  selectUserIdToFollowedUserId,
  selectUserIdToIgnoredUserId,
  selectIsFetchingRelationships
} from 'app/selectors/RelationshipsSelector';
import UserCard from './UserCard';

const UserCardContainer = ({
  hasAvatar,
  hasBadge,
  hasLibraryFullName,
  hasSmallUserName,
  overrideScreenReaderMessageUserName,
  trackProfileLinkClick,
  ugcType,
  user
}) => {
  const dispatch = useDispatch();
  const isFetchingRelationships = useSelector(selectIsFetchingRelationships);
  const appConfig = useSelector(selectAppConfig);
  const syncingFollowsByTargetUserId = useSelector(selectSyncingFollowsByTargetUserId);
  const syncingIgnoresByTargetUserId = useSelector(selectSyncingIgnoresByTargetUserId);
  const userIdToFollowedUserId = useSelector(selectUserIdToFollowedUserId);
  const userIdToIgnoredUserId = useSelector(selectUserIdToIgnoredUserId);

  const isMobileApp = appConfig.get('mobileApp') ?? false;
  const userId = user.get('id');
  const isFollowing = userIdToFollowedUserId.has(userId);
  const isIgnoring = userIdToIgnoredUserId.has(userId);
  const isSyncingFollowForTargetUser = isFetchingRelationships || syncingFollowsByTargetUserId.has(userId);
  const isSyncingIgnoreForTargetUser = isFetchingRelationships || syncingIgnoresByTargetUserId.has(userId);

  const toggleIsFollowing = () => {
    if (isFollowing) {
      const followId = userIdToFollowedUserId.get(userId, null);
      if (!followId) {
        throw new Error('A followId is required to unfollow a user');
      }

      dispatch(
        removeFollow({
          followId,
          targetUserId: userId
        })
      );
    } else {
      dispatch(
        createFollow({
          targetUserId: userId
        })
      );
    }
  };

  const toggleIsIgnoring = () => {
    if (isIgnoring) {
      const ignoreId = userIdToIgnoredUserId.get(userId, null);
      if (!ignoreId) {
        throw new Error('An ignoreId is required to unignore a user');
      }

      dispatch(
        removeIgnore({
          ignoreId,
          targetUserId: userId
        })
      );
    } else {
      dispatch(
        createIgnore({
          targetUserId: userId
        })
      );
    }
  };

  return (
    <UserCard
      hasAvatar={hasAvatar}
      hasBadge={hasBadge}
      hasLibraryFullName={hasLibraryFullName}
      hasSmallUserName={hasSmallUserName}
      isFollowing={isFollowing}
      isIgnoring={isIgnoring}
      isMobileApp={isMobileApp}
      isSyncingFollowForTargetUser={isSyncingFollowForTargetUser}
      isSyncingIgnoreForTargetUser={isSyncingIgnoreForTargetUser}
      overrideScreenReaderMessageUserName={overrideScreenReaderMessageUserName}
      toggleIsFollowing={toggleIsFollowing}
      toggleIsIgnoring={toggleIsIgnoring}
      trackProfileLinkClick={trackProfileLinkClick}
      ugcType={ugcType}
      user={user}
    />
  );
};

UserCardContainer.defaultProps = {
  hasAvatar: false,
  hasBadge: false,
  hasLibraryFullName: false,
  hasSmallUserName: false
};

UserCardContainer.propTypes = {
  hasAvatar: PropTypes.bool,
  hasBadge: PropTypes.bool,
  hasLibraryFullName: PropTypes.bool,
  hasSmallUserName: PropTypes.bool,
  overrideScreenReaderMessageUserName: PropTypes.string,
  trackProfileLinkClick: PropTypes.func,
  ugcType: PropTypes.oneOf(['comment', 'contentNotice', 'list', 'quotation', 'summary', 'video']).isRequired,
  user: userIdentityShape.isRequired
};

export default UserCardContainer;
